import React, { Component } from 'react'
import { withI18n, withI18nProps } from '@lingui/react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'

import compose from 'lodash/fp/compose'

import urlFor, { QueryParams } from 'shared/tools/url-helper'
import bookHelper from 'shared/tools/book-helper'

import {
  updateLibraryCard,
  removeLibraryCardGivenBook,
  removeSerialEposidesLibraryCards,
} from 'client/bookmate/reducers/library-cards-reducer'

import { show as showAuthPopup } from 'client/shared/reducers/popup-reducer'

import DeleteIcon from 'client/shared/icons/delete.svg'
import ShelfAddIcon from 'client/shared/icons/shelf-add.svg'
import LockIcon from 'client/shared/icons/lock.svg'
import UnlockIcon from 'client/shared/icons/unlock.svg'
import FlagIcon from 'client/shared/icons/flag.svg'
import ImpressionIcon from 'client/shared/icons/impression.svg'
import ReportIcon from 'client/shared/icons/exclam.svg'

import { ContextMenuButton } from 'client/shared/blocks/context-menu-button'

import { Dispatch, State } from 'shared/types/redux'
import { BookSerialProps } from 'client/shared/types/resource'
import {
  readInPrivate,
  markAsRead,
  removeFromWishlist,
} from 'client/shared/helpers/resource-helpers'
import { UserPrivacySettings } from '../types/current-user'
import { privacyApiConstants } from '../../bookmate/blocks/user-settings-privacy'
import { MenuItem } from './audiobook-context-menu-box'

type Props = {
  kind: 'default' | 'expand' | 'square'
  resource: BookSerialProps
  dispatch: Dispatch
  auth: boolean
  onStateChange: () => void
  privacySettings: UserPrivacySettings
  query?: QueryParams
} & withI18nProps

class BookContextMenuBox extends Component<Props> {
  static defaultProps = {
    kind: 'default',
  }

  getMenuData() {
    const {
      i18n,
      kind,
      resource,
      privacySettings,
      resource: { library_card, resourceType },
    } = this.props

    const canShowPrivacySettings =
      privacySettings.content_visibility === privacyApiConstants.booksShow

    const items: MenuItem[] = []

    if (!['expand'].includes(kind)) {
      items.push({
        title: i18n.t`menu.add_impression`,
        action: this.addOrEditImpression,
        disabled: false,
        icon: ImpressionIcon,
        actionName: 'add-impression',
      })
    }

    items.push({
      title: i18n.t`menu.add_to_shelf`,
      icon: ShelfAddIcon,
      action: this.onAddToShelfClick,
      actionName: 'add-to-shelf',
    })

    if (
      canShowPrivacySettings &&
      bookHelper.canBeRead(resource) &&
      !['expand'].includes(kind)
    ) {
      if (
        bookHelper.canBeRead(resource) &&
        bookHelper.isPrivate(library_card)
      ) {
        items.unshift({
          title: i18n.t`context-menu.read_in_public`,
          icon: UnlockIcon,
          action: this.onReadInPublicClick,
          actionName: 'read-in-public',
        })
      } else {
        items.unshift({
          title: i18n.t`context-menu.read_in_private`,
          icon: LockIcon,
          action: this.onReadInPrivateClick,
          actionName: 'read-in-private',
        })
      }
    }
    const isFinished = bookHelper.isFinished(library_card)

    if (
      (resourceType === 'serial' && resource.serialState
        ? resource.serialState.completed && !isFinished
        : !isFinished) &&
      kind !== 'expand'
    ) {
      items.push({
        title: i18n.t`menu.mark_as_read`,
        action: this.markAsRead,
        disabled: isFinished,
        icon: FlagIcon,
        actionName: 'mark-as-read',
      })
    }

    if (bookHelper.inWishlist(resource) && !['expand'].includes(kind)) {
      items.push({
        title: i18n.t`buttons.wishlist_remove_button`,
        icon: DeleteIcon,
        action: this.onRemoveFromWishlistClick,
        actionName: 'remove-from-wishlist',
      })
    }

    if (bookHelper.isAdded(resource)) {
      items.push({
        title: i18n.t`shared.remove`,
        action: this.onLibraryCardRemoveClick,
        icon: DeleteIcon,
        actionName: 'remove',
      })
    }

    if (!['expand'].includes(kind)) {
      items.push({
        title: i18n.t`context-menu.report_problem`,
        icon: ReportIcon,
        action: this.onReportClick,
        actionName: 'report',
      })
    }

    return { items }
  }

  onRemoveFromWishlistClick = () => {
    const { dispatch, resource } = this.props

    dispatch(removeFromWishlist[resource.resourceType](resource))
  }

  onReportClick = () => {
    const {
      dispatch,
      resource: { resourceType },
      query,
    } = this.props

    dispatch(push(urlFor.reportProblem(resourceType, query)))
  }

  onLibraryCardRemoveClick = () => {
    const { dispatch, resource, onStateChange } = this.props

    dispatch(removeLibraryCardGivenBook(resource))

    if (resource.resourceType === 'serial') {
      dispatch(removeSerialEposidesLibraryCards(resource))
    }
    if (onStateChange) {
      onStateChange()
    }
  }

  considerUnauthorizedClick = callback => {
    const { auth, dispatch } = this.props

    if (!auth) {
      return dispatch(showAuthPopup({ callback }))
    }

    return callback()
  }

  markAsRead = () => {
    const {
      dispatch,
      resource,
      resource: { library_card, resourceType },
    } = this.props

    this.considerUnauthorizedClick(() => {
      if (!bookHelper.isFinished(library_card)) {
        dispatch(
          markAsRead[resourceType](resource, library_card && library_card.uuid),
        )
      }
    })
  }

  addOrEditImpression = () => {
    const { resource, dispatch } = this.props

    this.considerUnauthorizedClick(() => {
      dispatch(
        push(urlFor[`${resource.resourceType}ImpressionForm`](resource.uuid)),
      )
    })
  }

  onAddToShelfClick = () => {
    const { resource, dispatch } = this.props

    this.considerUnauthorizedClick(() => {
      dispatch(
        push(urlFor[`${resource.resourceType}AddToShelf`](resource.uuid)),
      )
    })
  }

  onReadInPrivateClick = () => {
    const {
      dispatch,
      resource,
      resource: { resourceType, library_card },
    } = this.props

    this.considerUnauthorizedClick(() => {
      dispatch(
        readInPrivate[resourceType](
          resource,
          library_card ? library_card.uuid : null,
        ),
      )
    })
  }

  onReadInPublicClick = () => {
    const {
      dispatch,
      resource: {
        library_card: { uuid },
      },
    } = this.props

    dispatch(updateLibraryCard({ uuid, public: true }))
  }

  render() {
    const { kind } = this.props

    return <ContextMenuButton menuData={this.getMenuData()} kind={kind} />
  }
}

const wrapper = compose(
  connect((state: State) => ({
    auth: state.currentUser.auth,
    privacySettings: state.currentUser.ui.privacy.data,
    query: state.app.storedQuery,
  })),
  withI18n({ update: true }),
)

export default wrapper(BookContextMenuBox)
